import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  ClipboardList, 
  Plus, 
  Calendar,
  CheckCircle2,
  Circle,
  PlayCircle,
  ChefHat,
  Trash2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  FileText,
  Package,
  Users,
  Clock,
  MapPin,
  Printer,
  PlusCircle,
  ShoppingBag,
  Edit2,
  Truck,
  AlertTriangle
} from 'lucide-react';
import { computeTodayProductionSummary, computeFilteredForecastSummary, buildWeekDays, getOrderProgress } from './production/productionLogic';
import { buildChecklistHTML } from './production/printChecklist';
import { openPrintWindow } from '../../lib/printWindow';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { getOrderStatusBadgeClass } from '../../lib/statusHelpers';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { staffService } from '../../services/staffService';
import { toast } from 'sonner';
import { OrderPhotoUpload } from '../../components/verification/OrderPhotoUpload';
import { CustomOrderFormDialog } from '../../components/customOrders/CustomOrderFormDialog';
import { LocationLine } from '../../components/LocationLine';
import { locationHeading, locationLabel } from '../../lib/locationLabel';

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Circle, color: 'bg-gray-100 text-gray-700', textColor: 'text-gray-600' },
  in_progress: { label: 'In Progress', icon: PlayCircle, color: 'bg-blue-100 text-blue-700', textColor: 'text-blue-600' },
  completed: { label: 'Completed', icon: CheckCircle2, color: 'bg-green-100 text-green-700', textColor: 'text-green-600' }
};

// Compact per-status pills shown on each day card in the Forecast view.
// Order reflects workflow progression (earliest → latest stage).
const FORECAST_STATUS_PILLS = [
  { key: 'submitted',         short: 'S', label: 'Submitted',         className: 'bg-amber-100 text-amber-700' },
  { key: 'confirmed',         short: 'C', label: 'Confirmed',         className: 'bg-blue-100 text-blue-700' },
  { key: 'in_production',     short: 'P', label: 'In Production',     className: 'bg-orange-100 text-orange-700' },
  { key: 'ready_for_delivery',short: 'R', label: 'Ready for Delivery',className: 'bg-indigo-100 text-indigo-700' },
  { key: 'out_for_delivery',  short: 'O', label: 'Out for Delivery',  className: 'bg-purple-100 text-purple-700' },
];

// Staffing status for the day, mirroring the admin coverage board. Colour is
// never the only signal — the word is always there too.
const COVERAGE_DOTS = { red: 'bg-red-500', amber: 'bg-amber-500', green: 'bg-emerald-500' };
const COVERAGE_TEXT = { red: 'text-red-700', amber: 'text-amber-700', green: 'text-emerald-700' };
const COVERAGE_LABELS = { red: 'Not staffed', amber: 'Check', green: 'Covered' };

const StaffProduction = () => {
  const [activeTab, setActiveTab] = useState('today');
  const [batches, setBatches] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generatedList, setGeneratedList] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [selectedBatchOrders, setSelectedBatchOrders] = useState(new Set());
  
  // Forecast state
  const [forecastData, setForecastData] = useState(null);
  const [coverageByDate, setCoverageByDate] = useState({});
  const [forecastLoading, setForecastLoading] = useState(false);
  const [weekStartDate, setWeekStartDate] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(today.setDate(diff)).toISOString().split('T')[0];
  });
  
  // Forecast day detail dialog
  const [forecastDayDetailOpen, setForecastDayDetailOpen] = useState(false);
  const [forecastDayDetail, setForecastDayDetail] = useState(null);
  const [forecastDayDetailLoading, setForecastDayDetailLoading] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  
  // Print checklist state
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);

  // Re-sync dialog state
  const [resyncOpen, setResyncOpen] = useState(false);
  const [resyncBatchId, setResyncBatchId] = useState(null);
  const [resyncData, setResyncData] = useState(null);
  const [resyncLoading, setResyncLoading] = useState(false);
  const [resyncApplying, setResyncApplying] = useState(false);
  const [resyncSelectedAdds, setResyncSelectedAdds] = useState(new Set());
  
  // Custom order dialog
  const [customOrderOpen, setCustomOrderOpen] = useState(false);
  const [editingCustomOrder, setEditingCustomOrder] = useState(null);
  const [products, setProducts] = useState([]);

  const today = new Date().toISOString().split('T')[0];

  const loadProducts = useCallback(async () => {
    try {
      const data = await staffService.getStaffProducts();
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  }, []);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      const data = await staffService.getProductionBatches({ date: selectedDate });
      setBatches(data);
    } catch (error) {
      console.error('Error loading batches:', error);
      toast.error('Failed to load production batches');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const loadCustomOrders = useCallback(async () => {
    try {
      const data = await staffService.getCustomOrders(selectedDate);
      setCustomOrders(data);
    } catch (error) {
      console.error('Error loading custom orders:', error);
    }
  }, [selectedDate]);

  const loadForecast = useCallback(async () => {
    setForecastLoading(true);
    try {
      const endDate = new Date(weekStartDate);
      endDate.setDate(endDate.getDate() + 6);
      const endStr = endDate.toISOString().split('T')[0];
      const data = await staffService.getProductionForecast(weekStartDate, endStr);
      setForecastData(data);
      // Coverage is advisory here — the forecast must still render if it fails.
      try {
        const coverage = await staffService.getScheduleCoverage(weekStartDate, endStr);
        setCoverageByDate(
          Object.fromEntries((coverage.rows || []).map((row) => [row.delivery_date, row])),
        );
      } catch {
        setCoverageByDate({});
      }
    } catch (error) {
      console.error('Error loading forecast:', error);
      toast.error('Failed to load forecast');
    } finally {
      setForecastLoading(false);
    }
  }, [weekStartDate]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (activeTab === 'today') {
      loadBatches();
      loadCustomOrders();
    } else if (activeTab === 'forecast') {
      loadForecast();
    }
  }, [activeTab, loadBatches, loadCustomOrders, loadForecast]);

  const loadForecastDayDetail = async (date) => {
    setForecastDayDetailLoading(true);
    setForecastDayDetailOpen(true);
    try {
      const data = await staffService.getProductionDayDetail(date);
      setForecastDayDetail(data);
      setSelectedOrders(new Set(data.orders_by_client.map(o => o.order_id)));
    } catch (error) {
      console.error('Error loading day detail:', error);
      toast.error('Failed to load day details');
    } finally {
      setForecastDayDetailLoading(false);
    }
  };

  const handleGenerateList = async () => {
    setGenerating(true);
    try {
      const result = await staffService.generateProductionList(selectedDate);
      setGeneratedList(result);
      // Auto-select only orders that are NOT already in a batch
      const unbatchedOrderIds = new Set(
        result.orders
          .filter(o => !o.is_in_batch)
          .map(o => o.order_id)
      );
      setSelectedBatchOrders(unbatchedOrderIds);
    } catch (error) {
      toast.error('Failed to generate production list');
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateBatch = async () => {
    if (!generatedList || selectedBatchOrders.size === 0) {
      toast.error('No orders selected for batch');
      return;
    }

    // Filter to only include selected orders
    const ordersToInclude = generatedList.orders.filter(o => selectedBatchOrders.has(o.order_id));
    
    if (ordersToInclude.length === 0) {
      toast.error('No orders selected for batch');
      return;
    }

    try {
      await staffService.createProductionBatch({
        production_date: selectedDate,
        orders: ordersToInclude
      });
      toast.success('Production batch created');
      setGenerateDialogOpen(false);
      setGeneratedList(null);
      setSelectedBatchOrders(new Set());
      loadBatches();
      loadCustomOrders(); // Refresh custom orders display
    } catch (error) {
      toast.error('Failed to create batch');
    }
  };

  const toggleBatchOrderSelection = (orderId) => {
    const newSelected = new Set(selectedBatchOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedBatchOrders(newSelected);
  };

  const toggleAllBatchOrders = (selectUnbatchedOnly = false) => {
    if (!generatedList) return;
    
    if (selectUnbatchedOnly) {
      // Select only unbatched orders
      const unbatchedIds = new Set(
        generatedList.orders.filter(o => !o.is_in_batch).map(o => o.order_id)
      );
      setSelectedBatchOrders(unbatchedIds);
    } else if (selectedBatchOrders.size === generatedList.orders.filter(o => !o.is_in_batch).length) {
      // If all unbatched are selected, deselect all
      setSelectedBatchOrders(new Set());
    } else {
      // Select all unbatched
      const unbatchedIds = new Set(
        generatedList.orders.filter(o => !o.is_in_batch).map(o => o.order_id)
      );
      setSelectedBatchOrders(unbatchedIds);
    }
  };

  // Print checklist handler
  const handlePrintChecklist = async () => {
    setPrintLoading(true);
    try {
      const data = await staffService.getProductionDayDetail(selectedDate);
      setPrintData(data);
      setPrintDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load print data');
    } finally {
      setPrintLoading(false);
    }
  };

  const executePrint = () => {
    if (!printData) return;
    openPrintWindow(buildChecklistHTML(printData));
  };

  const handleCustomOrderSubmit = (payload, orderId) => (
    orderId
      ? staffService.updateCustomOrder(orderId, payload)
      : staffService.createCustomOrder(payload)
  );

  const handleCustomOrderSaved = () => {
    loadCustomOrders();
    loadBatches();
  };

  const customOrderProducts = useMemo(
    () => products.map((p) => ({
      id: p.id, name: p.name, pack_size: p.pack_size, price: p.wholesale_price,
    })),
    [products]
  );

  // Update order status within batch (order-level tracking)
  const handleUpdateOrderStatus = async (batchId, orderIndex, status) => {
    // Production photo gate: a regular order needs ≥1 packed-order photo
    const targetBatch = batches.find(b => b.id === batchId);
    const targetOrder = targetBatch?.orders?.[orderIndex];
    if (
      status === 'completed' &&
      targetOrder && !targetOrder.is_custom &&
      targetOrder.order_id && targetOrder.order_id !== 'legacy' &&
      (targetOrder.production_photos?.length || 0) === 0
    ) {
      toast.error('Add at least 1 photo of the packed order', { position: 'top-center' });
      return;
    }

    // Update local state immediately
    setBatches(prevBatches =>
      prevBatches.map(batch => {
        if (batch.id === batchId) {
          const updatedOrders = [...batch.orders];
          updatedOrders[orderIndex] = { ...updatedOrders[orderIndex], status };
          
          // Check if all orders complete
          const allComplete = updatedOrders.every(o => o.status === 'completed');
          return { 
            ...batch, 
            orders: updatedOrders,
            status: allComplete ? 'completed' : batch.status 
          };
        }
        return batch;
      })
    );
    
    try {
      await staffService.updateProductionOrder(batchId, orderIndex, { status });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update order status', { position: 'top-center', duration: 4000 });
      loadBatches();
    }
  };

  // Sync a photo change into the batches state
  const handleOrderPhotosChange = (batchId, orderIndex, photos) => {
    setBatches(prevBatches =>
      prevBatches.map(batch => {
        if (batch.id === batchId) {
          const updatedOrders = [...batch.orders];
          updatedOrders[orderIndex] = {
            ...updatedOrders[orderIndex],
            production_photos: photos,
            production_photos_count: photos.length,
          };
          return { ...batch, orders: updatedOrders };
        }
        return batch;
      })
    );
  };

  // Update item quantity within an order
  const handleUpdateItemQuantity = async (batchId, orderIndex, itemIndex, quantity) => {
    // Update local state immediately
    setBatches(prevBatches =>
      prevBatches.map(batch => {
        if (batch.id === batchId) {
          const updatedOrders = [...batch.orders];
          const updatedItems = [...updatedOrders[orderIndex].items];
          updatedItems[itemIndex] = { ...updatedItems[itemIndex], quantity_produced: quantity };
          updatedOrders[orderIndex] = { ...updatedOrders[orderIndex], items: updatedItems };
          return { ...batch, orders: updatedOrders };
        }
        return batch;
      })
    );
    
    try {
      await staffService.updateProductionOrder(batchId, orderIndex, { 
        item_index: itemIndex,
        quantity_produced: quantity 
      });
    } catch (error) {
      toast.error('Failed to save update');
      loadBatches();
    }
  };

  const handleDeleteBatch = async (batch) => {
    const batchId = typeof batch === 'string' ? batch : batch.id;
    const orderCount = typeof batch === 'string' ? 0 : (batch.orders?.length || 0);
    const msg = orderCount
      ? `This will delete the batch and return its ${orderCount} order${orderCount === 1 ? '' : 's'} to Confirmed so they can be batched again. Any production photos on those orders will be archived.\n\nDelete this batch?`
      : 'Delete this batch?';
    if (!window.confirm(msg)) return;
    try {
      const res = await staffService.deleteProductionBatch(batchId);
      toast.success('Batch deleted — orders returned to Confirmed');
      const skipped = res?.skipped || [];
      skipped.forEach((s) => {
        toast.warning(
          `Order ${s.order_number || s.order_id} was not reverted (${(s.why || '').replace(/_/g, ' ')})`,
          { duration: 5000 }
        );
      });
      loadBatches();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete batch');
    }
  };

  const handleOpenResync = async (batchId) => {
    setResyncBatchId(batchId);
    setResyncOpen(true);
    setResyncLoading(true);
    setResyncData(null);
    setResyncSelectedAdds(new Set());
    try {
      const data = await staffService.previewBatchResync(batchId);
      setResyncData(data);
      // Pre-select every new order by default (baker can uncheck).
      setResyncSelectedAdds(new Set((data.additions || []).map((a) => a.order_id)));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to check for new orders');
      setResyncOpen(false);
    } finally {
      setResyncLoading(false);
    }
  };

  const toggleResyncAddition = (orderId) => {
    setResyncSelectedAdds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const handleApplyResync = async () => {
    if (!resyncBatchId) return;
    setResyncApplying(true);
    try {
      const res = await staffService.resyncBatch(resyncBatchId, Array.from(resyncSelectedAdds));
      const added = res?.additions?.length || 0;
      const removed = res?.removals?.length || 0;
      const changed = res?.modifications?.length || 0;
      toast.success(
        `${added} order${added === 1 ? '' : 's'} added, ${removed} removed, ${changed} updated. Totals updated — reprint the checklist.`,
        { duration: 5000 }
      );
      setResyncOpen(false);
      setResyncData(null);
      setResyncBatchId(null);
      loadBatches();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to re-sync batch');
    } finally {
      setResyncApplying(false);
    }
  };

  const handleDeleteCustomOrder = async (orderId) => {
    if (!window.confirm('Delete this custom order?')) return;
    try {
      await staffService.deleteCustomOrder(orderId);
      toast.success('Custom order deleted');
      loadCustomOrders();
    } catch (error) {
      toast.error('Failed to delete custom order');
    }
  };

  const navigateWeek = (direction) => {
    const current = new Date(weekStartDate);
    current.setDate(current.getDate() + (direction * 7));
    setWeekStartDate(current.toISOString().split('T')[0]);
  };

  const toggleOrderSelection = (orderId) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrders(newSelected);
  };

  const toggleAllOrders = () => {
    if (!forecastDayDetail) return;
    if (selectedOrders.size === forecastDayDetail.orders_by_client.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(forecastDayDetail.orders_by_client.map(o => o.order_id)));
    }
  };

  // Calculate summary from batches (order-centric)
  const todayProductionSummary = useMemo(
    () => computeTodayProductionSummary(batches),
    [batches]
  );

  // Calculate filtered summary for forecast
  const filteredSummaryByCategory = useMemo(
    () => computeFilteredForecastSummary(forecastDayDetail, selectedOrders),
    [forecastDayDetail, selectedOrders]
  );

  // Generate week days for calendar
  const weekDays = useMemo(
    () => buildWeekDays(weekStartDate, today),
    [weekStartDate, today]
  );

  const getOrdersForDay = (date) => {
    if (!forecastData) return null;
    return forecastData.days.find(d => d.date === date);
  };

  const openNewCustomOrder = () => {
    setEditingCustomOrder(null);
    setCustomOrderOpen(true);
  };

  const openEditCustomOrder = async (orderId) => {
    try {
      const order = await staffService.getCustomOrder(orderId);
      setEditingCustomOrder(order);
      setCustomOrderOpen(true);
    } catch (error) {
      toast.error('Failed to load custom order');
    }
  };

  if (loading && activeTab === 'today') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-production">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Production</h1>
          <p className="text-sm text-gray-600 mt-1">Manage daily production by order</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="today" className="flex min-w-0 items-center gap-2">
            <ClipboardList className="h-4 w-4 shrink-0" />
            <span className="truncate">Today's Production</span>
          </TabsTrigger>
          <TabsTrigger value="forecast" className="flex min-w-0 items-center gap-2">
            <Calendar className="h-4 w-4 shrink-0" />
            <span className="truncate">Forecast</span>
          </TabsTrigger>
        </TabsList>

        {/* Today's Production Tab */}
        <TabsContent value="today" className="space-y-4 mt-4">
          {/* Controls */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-40"
                  data-testid="production-date-picker"
                />
              </div>
              {selectedDate === today && (
                <Badge className="bg-green-100 text-green-700">Today</Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={handlePrintChecklist}
                disabled={printLoading || batches.length === 0}
                data-testid="print-checklist-btn"
              >
                <Printer className="h-4 w-4 mr-2" />
                {printLoading ? 'Loading...' : 'Print Checklist'}
              </Button>
              <Button
                variant="outline"
                onClick={openNewCustomOrder}
                data-testid="add-custom-order-btn"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Custom Order
              </Button>
              <Button
                onClick={() => {
                  setGeneratedList(null);
                  setSelectedBatchOrders(new Set());
                  setGenerateDialogOpen(true);
                }}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="new-batch-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Batch
              </Button>
            </div>
          </div>

          {/* Custom Orders for this date - only show unbatched orders */}
          {customOrders.filter(o => !o.is_in_batch).length > 0 && (
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingBag className="h-5 w-5 text-purple-600" />
                  Custom Orders for {selectedDate === today ? 'Today' : selectedDate} ({customOrders.filter(o => !o.is_in_batch).length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {customOrders.filter(o => !o.is_in_batch).map((order) => (
                    <div key={order.id} className="p-3 bg-white rounded-lg border">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{order.customer_name}</span>
                            <Badge variant="outline" className="text-xs">{order.order_number}</Badge>
                            <Badge className={`text-xs ${
                              order.order_type === 'delivery' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {order.order_type === 'delivery' ? <><Truck className="h-3 w-3 mr-1 inline" />Delivery</> : 'Pickup'}
                            </Badge>
                            <Badge className={`text-xs ${getOrderStatusBadgeClass(order.status)}`}>
                              {order.status?.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            {order.items.map(i => `${i.product_name} x${i.quantity}${i.unit_price ? ` ($${i.unit_price})` : ''}`).join(', ')}
                          </div>
                          {order.total_amount > 0 && (
                            <div className="text-xs font-medium text-gray-600 mt-0.5">Total: ${order.total_amount.toFixed(2)}</div>
                          )}
                          {order.order_type === 'delivery' && order.delivery_address && (
                            <div className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" /> {[order.delivery_address, order.delivery_city, order.delivery_state, order.delivery_zip].filter(Boolean).join(', ')}
                            </div>
                          )}
                          {order.order_type === 'pickup' && order.pickup_time && (
                            <div className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                              <Clock className="h-3 w-3" /> Pickup: {order.pickup_time}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditCustomOrder(order.id)}
                            className="text-blue-500"
                            data-testid={`edit-custom-order-${order.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCustomOrder(order.id)}
                            className="text-red-500"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-purple-600 mt-2">
                  Click "New Batch" to include these in production
                </p>
              </CardContent>
            </Card>
          )}

          {/* Production Summary */}
          {batches.length > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="h-5 w-5 text-orange-600" />
                  Production Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center p-3 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">{todayProductionSummary.total_donuts}</div>
                    <div className="text-xs text-gray-500">Total Needed</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{todayProductionSummary.total_produced}</div>
                    <div className="text-xs text-gray-500">Produced</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {todayProductionSummary.orders_complete}/{todayProductionSummary.total_orders}
                    </div>
                    <div className="text-xs text-gray-500">Orders Complete</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{todayProductionSummary.categories.length}</div>
                    <div className="text-xs text-gray-500">Donut Types</div>
                  </div>
                </div>
                
                {/* Summary by Donut Type */}
                <div className="bg-white rounded-lg p-3">
                  <h4 className="font-medium mb-2 text-sm text-gray-700">By Donut Type:</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {todayProductionSummary.categories.map((cat) => (
                      <div key={cat.category} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <span className="text-sm font-medium capitalize">{cat.category}</span>
                        <div className="text-right">
                          <div className="text-lg font-bold text-orange-600">{cat.quantity_needed}</div>
                          {cat.quantity_produced > 0 && (
                            <div className="text-xs text-green-600">{cat.quantity_produced} done</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Production Batches - ORDER CENTRIC */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Production Orders</h3>
            {batches.length > 0 ? (
              batches.map((batch) => (
                <Card key={batch.id} data-testid={`batch-card-${batch.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-100 rounded-lg">
                          <ClipboardList className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Production Batch</CardTitle>
                          <p className="text-sm text-gray-500">
                            {batch.production_date} • {batch.orders?.length || 0} orders
                          </p>
                          {batch.needs_resync && (
                            <p className="text-xs text-amber-700 mt-0.5 font-medium" data-testid={`batch-needs-resync-${batch.id}`}>
                              An order in this batch was edited — check for changes to pick it up.
                            </p>
                          )}
                          {batch.last_resynced_at && (
                            <p className="text-xs text-gray-400 mt-0.5" data-testid={`batch-resynced-${batch.id}`}>
                              Last synced {new Date(batch.last_resynced_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={STATUS_CONFIG[batch.status]?.color || 'bg-gray-100'}>
                          {STATUS_CONFIG[batch.status]?.label || batch.status}
                        </Badge>
                        {batch.status === 'pending' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenResync(batch.id)}
                            className={`h-10 min-w-[44px] ${batch.needs_resync
                              ? 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
                              : 'text-orange-600 border-orange-200 hover:bg-orange-50'}`}
                            data-testid={`resync-batch-${batch.id}`}
                            title="Check for orders booked or edited after this batch was planned"
                          >
                            <RefreshCw className="h-4 w-4 sm:mr-2" />
                            <span className="hidden sm:inline">
                              {batch.needs_resync ? 'Check for changes' : 'Check for new orders'}
                            </span>
                          </Button>
                        )}
                        {batch.status === 'completed' ? null : batch.status === 'in_progress' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled
                            className="h-10 min-w-[44px] text-gray-300 cursor-not-allowed"
                            data-testid={`delete-batch-${batch.id}`}
                            title="Set the batch back to planned before deleting it"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteBatch(batch)}
                            className="h-10 min-w-[44px] text-red-500"
                            data-testid={`delete-batch-${batch.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {batch.orders?.map((order, orderIndex) => {
                        const progress = getOrderProgress(order);
                        const isRegularOrder = !order.is_custom && order.order_id && order.order_id !== 'legacy';
                        const photoCount = order.production_photos?.length || 0;
                        const needsPhoto = isRegularOrder && photoCount === 0;
                        const canManagePhotos = isRegularOrder && ['submitted', 'confirmed', 'in_production', undefined].includes(order.order_db_status);
                        return (
                          <div 
                            key={order.order_id || orderIndex} 
                            className={`border rounded-lg overflow-hidden ${
                              order.status === 'completed' ? 'bg-green-50 border-green-200' : 'bg-white'
                            }`}
                          >
                            {/* Order Header */}
                            <div className="p-3 bg-gray-50 border-b flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{order.client_name}</span>
                                    {order.is_custom && (
                                      <Badge className="bg-purple-100 text-purple-700 text-xs">Custom</Badge>
                                    )}
                                    <Badge variant="outline" className="text-xs">{order.order_number}</Badge>
                                  </div>
                                  {order.delivery_time && (
                                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                      <Clock className="h-3 w-3" /> {order.delivery_time}
                                    </div>
                                  )}
                                  <LocationLine
                                    name={order.location_name}
                                    source={order.location_name_source}
                                    clientName={order.client_name}
                                    className="mt-1"
                                    testId={`batch-order-location-${order.order_id && order.order_id !== 'legacy' ? order.order_id : 'legacy-' + orderIndex}`}
                                  />
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {/* Progress indicator */}
                                <div className="text-right">
                                  <div className="text-sm font-medium">
                                    {progress.totalProduced}/{progress.totalNeeded}
                                  </div>
                                  <div className="text-xs text-gray-500">{progress.percentage}%</div>
                                </div>
                                {/* Order Status */}
                                <Select
                                  value={order.status || 'pending'}
                                  onValueChange={(v) => handleUpdateOrderStatus(batch.id, orderIndex, v)}
                                >
                                  <SelectTrigger 
                                    className={`w-36 ${STATUS_CONFIG[order.status]?.textColor || ''}`}
                                    data-testid={`order-status-${batch.id}-${orderIndex}`}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                    <SelectItem value="completed" disabled={needsPhoto}>Completed</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            {/* Production photo gate (regular wholesale orders) */}
                            {isRegularOrder && (
                              <div className="px-3 py-3 border-b bg-white">
                                <OrderPhotoUpload
                                  orderId={order.order_id}
                                  kind="production"
                                  testId={`production-photos-${order.order_id}`}
                                  photos={order.production_photos || []}
                                  canRemove={canManagePhotos}
                                  onPhotosChange={(photos) => handleOrderPhotosChange(batch.id, orderIndex, photos)}
                                  label="Packed order photos"
                                />
                                {needsPhoto && order.status !== 'completed' && (
                                  <p className="text-xs text-amber-600 mt-2 flex items-center gap-1" data-testid={`photo-required-${order.order_id}`}>
                                    <AlertTriangle className="h-3 w-3" />
                                    Add at least 1 photo of the packed order before marking it complete
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {/* Order Items */}
                            <div className="p-3">
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-xs text-gray-500 uppercase">
                                      <th className="text-left p-2">Product</th>
                                      <th className="text-center p-2">Type</th>
                                      <th className="text-center p-2">Needed</th>
                                      <th className="text-center p-2">Produced</th>
                                      <th className="text-center p-2">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {order.items?.map((item, itemIndex) => {
                                      const needed = item.quantity_needed || item.quantity || 0;
                                      const produced = item.quantity_produced || 0;
                                      const itemComplete = needed > 0 && produced >= needed;
                                      return (
                                        <tr key={itemIndex} className="border-t">
                                          <td className="p-2">
                                            <div className="flex items-center gap-2">
                                              <ChefHat className="h-4 w-4 text-orange-500" />
                                              {item.product_name}
                                            </div>
                                          </td>
                                          <td className="p-2 text-center capitalize text-gray-500">
                                            {item.category || 'other'}
                                          </td>
                                          <td className="p-2 text-center font-medium">{needed}</td>
                                          <td className="p-2 text-center">
                                            <Input
                                              type="number"
                                              value={produced}
                                              onChange={(e) => handleUpdateItemQuantity(
                                                batch.id, orderIndex, itemIndex, 
                                                parseInt(e.target.value) || 0
                                              )}
                                              className="w-16 mx-auto text-center h-8"
                                              min={0}
                                              data-testid={`produced-${batch.id}-${orderIndex}-${itemIndex}`}
                                            />
                                          </td>
                                          <td className="p-2 text-center">
                                            {itemComplete ? (
                                              <Badge className="bg-green-100 text-green-700 text-xs">
                                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                                Done
                                              </Badge>
                                            ) : (
                                              <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                                                {needed - produced} left
                                              </Badge>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <ClipboardList className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Production Batches</h3>
                    <p className="text-gray-500 mb-4">Generate a production list from orders for {selectedDate}</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      <Button
                        variant="outline"
                        onClick={openNewCustomOrder}
                      >
                        <PlusCircle className="h-4 w-4 mr-2" />
                        Add Custom Order
                      </Button>
                      <Button
                        onClick={() => {
                          setGeneratedList(null);
                          setGenerateDialogOpen(true);
                        }}
                        className="bg-orange-500 hover:bg-orange-600"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create Production Batch
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Forecast Tab */}
        <TabsContent value="forecast" className="space-y-4 mt-4">
          {/* Week Navigation */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg">Weekly Forecast</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-medium min-w-[150px] sm:min-w-[180px] text-center">
                    {new Date(weekStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {
                      new Date(new Date(weekStartDate).setDate(new Date(weekStartDate).getDate() + 6))
                        .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    }
                  </span>
                  <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {forecastLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="grid grid-cols-7 gap-2 min-w-[640px] sm:min-w-0">
                  {weekDays.map((day) => {
                    const dayData = getOrdersForDay(day.date);
                    const menuSpecials = dayData?.menu_specials;
                    const hasOrders = dayData?.orders?.length > 0;
                    const hasMenu = Boolean(menuSpecials);
                    const coverage = coverageByDate[day.date];
                    return (
                      <div
                        key={day.date}
                        onClick={() => loadForecastDayDetail(day.date)}
                        data-testid={`forecast-day-${day.date}`}
                        className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                          day.isToday 
                            ? 'border-orange-500 bg-orange-50' 
                            : hasOrders 
                              ? 'border-blue-200 bg-blue-50 hover:border-blue-400'
                              : hasMenu
                                ? 'border-amber-200 bg-amber-50 hover:border-amber-400'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`forecast-day-${day.date}`}
                      >
                        <div className="text-center">
                          <div className={`text-xs font-medium ${day.isToday ? 'text-orange-600' : 'text-gray-500'}`}>
                            {day.dayName}
                          </div>
                          <div className={`text-lg font-bold ${day.isToday ? 'text-orange-600' : 'text-gray-900'}`}>
                            {day.dayNum}
                          </div>
                          {coverage && (
                            <div
                              className="mt-1 flex items-center justify-center gap-1"
                              title={coverage.reasons?.length
                                ? coverage.reasons.join(' · ')
                                : `Covered — ${coverage.bakers} baker(s), ${coverage.drivers} driver(s)`}
                              data-testid={`forecast-coverage-${day.date}`}
                            >
                              <span className={`inline-block h-2 w-2 rounded-full ${COVERAGE_DOTS[coverage.level] || COVERAGE_DOTS.green}`} />
                              <span className={`text-[10px] font-semibold uppercase tracking-wide ${COVERAGE_TEXT[coverage.level] || COVERAGE_TEXT.green}`}>
                                {COVERAGE_LABELS[coverage.level] || 'Covered'}
                              </span>
                            </div>
                          )}
                          {hasOrders ? (
                            <div className="mt-2 space-y-1">
                              <Badge className="bg-blue-100 text-blue-700 text-xs">
                                {dayData.orders.length} orders
                              </Badge>
                              <div className="text-xs text-gray-500">
                                {dayData.total_donuts} donuts
                              </div>
                              {dayData.status_breakdown && (
                                <div className="flex flex-wrap gap-1 justify-center pt-1" data-testid={`status-breakdown-${day.date}`}>
                                  {FORECAST_STATUS_PILLS
                                    .filter(s => (dayData.status_breakdown[s.key] || 0) > 0)
                                    .map(s => (
                                      <span
                                        key={s.key}
                                        title={`${s.label}: ${dayData.status_breakdown[s.key]}`}
                                        className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded text-[10px] font-semibold ${s.className}`}
                                      >
                                        {s.short}
                                        <span className="ml-0.5">{dayData.status_breakdown[s.key]}</span>
                                      </span>
                                    ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            !hasMenu && <div className="mt-2 text-xs text-gray-400">No orders</div>
                          )}
                          {hasMenu && (
                            <div
                              className="mt-2 rounded-md bg-amber-100 border border-amber-200 px-1.5 py-1"
                              title={`${menuSpecials.title || 'Scheduled menu'} — ${menuSpecials.product_count} ${menuSpecials.product_count === 1 ? 'special' : 'specials'}`}
                              data-testid={`forecast-day-menu-chip-${day.date}`}
                            >
                              <div className="flex items-center justify-center gap-1 text-[10px] font-semibold text-amber-800 uppercase tracking-wide">
                                <span>🍩</span>
                                <span>{menuSpecials.product_count} special{menuSpecials.product_count === 1 ? '' : 's'}</span>
                              </div>
                              {menuSpecials.total_committed > 0 && (
                                <div className="text-[10px] text-amber-700 mt-0.5">
                                  {menuSpecials.total_committed} committed
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary Stats */}
          {forecastData && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <FileText className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Orders</p>
                      <p className="text-2xl font-bold">{forecastData.total_orders}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-orange-100 rounded-lg">
                      <Package className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Donuts</p>
                      <p className="text-2xl font-bold">
                        {forecastData.days.reduce((sum, d) => sum + d.total_donuts, 0)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-100 rounded-lg">
                      <Calendar className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Active Days</p>
                      <p className="text-2xl font-bold">
                        {forecastData.days.filter(d => d.orders?.length > 0).length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Generate Batch Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generate Production Batch</DialogTitle>
            <DialogDescription>
              Select orders to include in this production batch.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-gray-600">
                Generate production for:
              </p>
              <p className="font-bold text-lg mt-1">
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                })}
              </p>
            </div>

            {!generatedList ? (
              <Button
                onClick={handleGenerateList}
                disabled={generating}
                className="w-full bg-orange-500 hover:bg-orange-600"
                data-testid="generate-from-orders-btn"
              >
                {generating ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Generate from Orders
                  </>
                )}
              </Button>
            ) : (
              <>
                {/* Summary - only for selected unbatched orders */}
                {generatedList.summary && (
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-medium">{generatedList.order_count} orders found</span>
                        {generatedList.unbatched_count < generatedList.order_count && (
                          <span className="text-sm text-gray-500 ml-2">
                            ({generatedList.order_count - generatedList.unbatched_count} already in batch)
                          </span>
                        )}
                      </div>
                      <span className="text-orange-600 font-bold">{selectedBatchOrders.size} selected</span>
                    </div>
                    {selectedBatchOrders.size > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {(() => {
                          // Calculate summary for selected orders only
                          const selectedSummary = {};
                          let selectedTotal = 0;
                          generatedList.orders
                            .filter(o => selectedBatchOrders.has(o.order_id))
                            .forEach(o => {
                              o.items.forEach(item => {
                                const cat = item.category || 'other';
                                selectedSummary[cat] = (selectedSummary[cat] || 0) + item.quantity_needed;
                                selectedTotal += item.quantity_needed;
                              });
                            });
                          return (
                            <>
                              <Badge className="bg-green-100 text-green-700">{selectedTotal} donuts</Badge>
                              {Object.entries(selectedSummary).sort().map(([cat, qty]) => (
                                <Badge key={cat} className="bg-white text-orange-700 capitalize">
                                  {cat}: {qty}
                                </Badge>
                              ))}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* Selection controls */}
                {generatedList.orders.length > 0 && (
                  <div className="flex items-center justify-between">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => toggleAllBatchOrders()}
                    >
                      {selectedBatchOrders.size === generatedList.orders.filter(o => !o.is_in_batch).length 
                        ? 'Deselect All' 
                        : 'Select All Unbatched'}
                    </Button>
                    <div className="text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1 mr-3">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span> Not in batch
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-gray-400"></span> Already batched
                      </span>
                    </div>
                  </div>
                )}

                {/* Orders list with checkboxes - unbatched orders shown first */}
                <div className="border rounded-lg max-h-[350px] overflow-y-auto">
                  {generatedList.orders.length > 0 ? (
                    <>
                      {/* Section: Available to batch */}
                      {generatedList.orders.some(o => !o.is_in_batch) && (
                        <div className="px-3 py-2 bg-green-100 text-green-800 font-medium text-sm sticky top-0">
                          ✓ Available to add to batch ({generatedList.orders.filter(o => !o.is_in_batch).length})
                        </div>
                      )}
                      {[...generatedList.orders]
                        .filter(o => !o.is_in_batch)
                        .map((order) => {
                          const isSelected = selectedBatchOrders.has(order.order_id);
                          return (
                            <div 
                              key={order.order_id} 
                              className={`p-3 cursor-pointer transition-colors border-b ${
                                isSelected 
                                  ? 'bg-green-50 border-l-4 border-l-green-500' 
                                  : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                              }`}
                              onClick={() => toggleBatchOrderSelection(order.order_id)}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`flex items-center justify-center w-6 h-6 rounded border-2 ${
                                  isSelected 
                                    ? 'border-green-500 bg-green-500' 
                                    : 'border-gray-400 bg-white hover:border-green-400'
                                }`}>
                                  {isSelected && (
                                    <CheckCircle2 className="h-4 w-4 text-white" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">{order.client_name}</span>
                                      {order.is_custom && (
                                        <Badge className="bg-purple-100 text-purple-700 text-xs">Custom</Badge>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {order.delivery_time && (
                                        <span className="text-xs text-gray-500">{order.delivery_time}</span>
                                      )}
                                      <Badge variant="outline" className="text-xs">{order.order_number}</Badge>
                                    </div>
                                  </div>
                                  <div className="flex gap-2 flex-wrap">
                                    {order.items.map((item, j) => (
                                      <Badge key={`${order.order_id}-${item.product_id || item.product_name}-${j}`} variant="outline" className="text-xs">
                                        {item.product_name}: {item.quantity_needed}
                                      </Badge>
                                    ))}
                                  </div>
                                  <LocationLine
                                    name={order.location_name}
                                    source={order.location_name_source}
                                    clientName={order.client_name}
                                    className="mt-1"
                                    testId={`generated-order-location-${order.order_id}`}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      
                      {/* Section: Already in batch */}
                      {generatedList.orders.some(o => o.is_in_batch) && (
                        <div className="px-3 py-2 bg-gray-200 text-gray-600 font-medium text-sm sticky top-0">
                          Already in batch ({generatedList.orders.filter(o => o.is_in_batch).length})
                        </div>
                      )}
                      {[...generatedList.orders]
                        .filter(o => o.is_in_batch)
                        .map((order) => (
                          <div 
                            key={order.order_id} 
                            className="p-3 bg-gray-50 opacity-60 cursor-not-allowed border-b"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex items-center justify-center w-6 h-6 rounded border-2 border-gray-300 bg-gray-200">
                                <span className="text-gray-400 text-xs">—</span>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{order.client_name}</span>
                                    {order.is_custom && (
                                      <Badge className="bg-purple-100 text-purple-700 text-xs">Custom</Badge>
                                    )}
                                    <Badge className="bg-gray-300 text-gray-600 text-xs">In Batch</Badge>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {order.delivery_time && (
                                      <span className="text-xs text-gray-500">{order.delivery_time}</span>
                                    )}
                                    <Badge variant="outline" className="text-xs">{order.order_number}</Badge>
                                  </div>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                  {order.items.map((item, j) => (
                                    <Badge key={`${order.order_id}-${item.product_id || item.product_name}-${j}`} variant="outline" className="text-xs">
                                      {item.product_name}: {item.quantity_needed}
                                    </Badge>
                                  ))}
                                </div>
                                <LocationLine
                                  name={order.location_name}
                                  source={order.location_name_source}
                                  clientName={order.client_name}
                                  className="mt-1"
                                  testId={`batched-order-location-${order.order_id}`}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                    </>
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      No orders found for this date
                    </div>
                  )}
                </div>

                {selectedBatchOrders.size > 0 && (
                  <Button
                    onClick={handleCreateBatch}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="create-batch-btn"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Create Production Batch ({selectedBatchOrders.size} orders)
                  </Button>
                )}
                
                {selectedBatchOrders.size === 0 && generatedList.orders.length > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center text-amber-700 text-sm">
                    Select at least one order to create a batch
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Order Dialog */}
      <CustomOrderFormDialog
        open={customOrderOpen}
        onOpenChange={(open) => { setCustomOrderOpen(open); if (!open) setEditingCustomOrder(null); }}
        order={editingCustomOrder}
        products={customOrderProducts}
        defaultDate={selectedDate}
        onSubmit={handleCustomOrderSubmit}
        onSaved={handleCustomOrderSaved}
      />

      {/* Forecast Day Detail Dialog */}
      <Dialog open={forecastDayDetailOpen} onOpenChange={setForecastDayDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>
                {forecastDayDetail && new Date(forecastDayDetail.date + 'T12:00:00').toLocaleDateString('en-US', {
                  weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                })}
              </span>
            </DialogTitle>
            <DialogDescription>
              View production details. Select/deselect orders to customize.
            </DialogDescription>
          </DialogHeader>
          
          {forecastDayDetailLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : forecastDayDetail && (
            <div className="space-y-6">
              {/* Summary */}
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="h-5 w-5 text-orange-600" />
                    Summary
                    {selectedOrders.size < forecastDayDetail.orders_by_client.length && (
                      <Badge variant="outline" className="ml-2">
                        {selectedOrders.size}/{forecastDayDetail.orders_by_client.length} selected
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center p-3 bg-white rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">{filteredSummaryByCategory.total_donuts}</div>
                      <div className="text-xs text-gray-500">Total Donuts</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{selectedOrders.size}</div>
                      <div className="text-xs text-gray-500">Orders</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{filteredSummaryByCategory.categories.length}</div>
                      <div className="text-xs text-gray-500">Types</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">{filteredSummaryByCategory.products.length}</div>
                      <div className="text-xs text-gray-500">Products</div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3">
                    <h4 className="font-medium mb-2 text-sm text-gray-700">By Type:</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {filteredSummaryByCategory.categories.map((cat) => (
                        <div key={cat.category} className="flex items-center justify-between p-2 bg-orange-50 rounded border border-orange-200">
                          <span className="text-sm capitalize">{cat.category}</span>
                          <span className="font-bold text-orange-600">{cat.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* What the bakers actually count: one line per product, with
                      every walk-in's dozen folded in. */}
                  <div className="bg-white rounded-lg p-3 mt-3" data-testid="day-detail-product-totals">
                    <h4 className="font-medium mb-2 text-sm text-gray-700">By Product:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {filteredSummaryByCategory.products.map((p) => (
                        <div
                          key={p.product_id || p.product_name}
                          className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded border"
                          data-testid={`day-detail-product-${p.product_id || p.product_name}`}
                        >
                          <span className="text-sm min-w-0 truncate">
                            {p.product_name}
                            {p.is_one_off && (
                              <span className="ml-1.5 text-[10px] uppercase tracking-wide text-gray-400 border rounded px-1 py-0.5">
                                one-off
                              </span>
                            )}
                          </span>
                          <span className="font-bold text-gray-900 shrink-0">{p.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Menu Specials (Phase 3) */}
              {forecastDayDetail.menu_specials && (
                <Card className="border-amber-200 bg-amber-50" data-testid="forecast-day-menu-specials">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span>🍩</span>
                      Menu Specials
                      <Badge variant="outline" className="ml-2 bg-white">
                        {forecastDayDetail.menu_specials.product_count} scheduled
                      </Badge>
                      {forecastDayDetail.menu_specials.mode === 'exclusive' ? (
                        <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Exclusive</Badge>
                      ) : (
                        <Badge variant="secondary">Additive</Badge>
                      )}
                    </CardTitle>
                    {forecastDayDetail.menu_specials.title && (
                      <div className="text-sm text-amber-900 font-medium">
                        {forecastDayDetail.menu_specials.title}
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {forecastDayDetail.menu_specials.products.map((sp) => {
                        const pct = sp.cap_pct;
                        return (
                          <div
                            key={sp.product_id || sp.name}
                            className="p-3 bg-white rounded-lg border border-amber-200"
                            data-testid={`menu-special-row-${sp.product_id || sp.name}`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900 truncate">{sp.name}</div>
                                <div className="text-xs text-gray-500 capitalize">{sp.category}</div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="text-lg font-bold text-amber-700">
                                  {sp.qty_committed}
                                </div>
                                <div className="text-[10px] text-gray-500">
                                  {sp.qty_limit ? `of ${sp.qty_limit} cap` : 'committed'}
                                </div>
                              </div>
                            </div>
                            {sp.qty_limit ? (
                              <div className="w-full bg-amber-100 rounded-full h-1.5 mt-2 overflow-hidden">
                                <div
                                  className={`h-1.5 rounded-full transition-all ${
                                    (pct || 0) >= 100
                                      ? 'bg-red-500'
                                      : (pct || 0) >= 80
                                        ? 'bg-orange-500'
                                        : 'bg-amber-500'
                                  }`}
                                  style={{ width: `${Math.min(pct || 0, 100)}%` }}
                                />
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-amber-800 mt-3">
                      Menu commitments are shown <span className="font-semibold">separately</span> — they are NOT added to ordered totals above. Reservations become real orders when clients submit them.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Orders */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-600" />
                      Orders
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={toggleAllOrders}>
                      {selectedOrders.size === forecastDayDetail.orders_by_client.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {forecastDayDetail.orders_by_client.map((order) => (
                      <div
                        key={order.order_id}
                        className={`border rounded-lg overflow-hidden transition-all ${
                          selectedOrders.has(order.order_id) 
                            ? 'border-blue-300 bg-blue-50' 
                            : 'border-gray-200 bg-gray-50 opacity-60'
                        }`}
                      >
                        <div className="p-3 bg-white border-b flex items-center gap-3">
                          <Checkbox
                            checked={selectedOrders.has(order.order_id)}
                            onCheckedChange={() => toggleOrderSelection(order.order_id)}
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{order.client_name}</span>
                              <Badge variant="outline">{order.order_number}</Badge>
                            </div>
                            {order.delivery_time && (
                              <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                <Clock className="h-3 w-3" /> {order.delivery_time}
                              </div>
                            )}
                            <LocationLine
                              name={order.location_name}
                              source={order.location_name_source}
                              clientName={order.client_name}
                              address={order.delivery_address}
                              className="mt-1"
                              testId={`day-detail-order-location-${order.order_id}`}
                            />
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-orange-600">{order.total_donuts}</div>
                            <div className="text-xs text-gray-500">donuts</div>
                          </div>
                        </div>
                        <div className="p-3">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {order.items.map((item, idx) => (
                              <div key={item.product_id || `${item.product_name}-${idx}`} className="flex items-center justify-between text-sm p-2 bg-white rounded">
                                <span className="min-w-0 truncate">
                                  {item.product_name}
                                  {item.is_one_off && (
                                    <span className="ml-1.5 text-[10px] uppercase tracking-wide text-gray-400 border rounded px-1 py-0.5">
                                      one-off
                                    </span>
                                  )}
                                </span>
                                <span className="font-medium shrink-0">{item.quantity}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Checklist Dialog */}
      {/* Re-sync batch dialog */}
      <Dialog open={resyncOpen} onOpenChange={(open) => { if (!open && !resyncApplying) { setResyncOpen(false); setResyncData(null); setResyncBatchId(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="resync-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-orange-600" />
              Check for new orders
            </DialogTitle>
            <DialogDescription>
              Update this batch to match the orders booked for its production date. Removed orders return to Confirmed; added orders move into production.
            </DialogDescription>
          </DialogHeader>

          {resyncLoading ? (
            <div className="py-10 text-center text-gray-500" data-testid="resync-loading">
              <RefreshCw className="h-6 w-6 mx-auto animate-spin mb-2" />
              Checking current orders…
            </div>
          ) : resyncData ? (
            <div className="space-y-5">
              {/* Totals delta — the number bakers care about most */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3" data-testid="resync-totals">
                <p className="text-sm font-semibold text-orange-800 mb-2">Donut totals · before → after</p>
                {(() => {
                  const before = resyncData.item_totals_before || [];
                  const after = resyncData.item_totals_after || [];
                  const names = Array.from(new Set([...before, ...after].map((t) => t.product_name))).sort();
                  const bMap = Object.fromEntries(before.map((t) => [t.product_name, t.quantity]));
                  const aMap = Object.fromEntries(after.map((t) => [t.product_name, t.quantity]));
                  if (names.length === 0) return <p className="text-sm text-gray-500">No items.</p>;
                  return (
                    <div className="space-y-1">
                      {names.map((n) => {
                        const b = bMap[n] || 0; const a = aMap[n] || 0;
                        const changed = a !== b;
                        return (
                          <div key={n} className={`flex justify-between text-sm ${changed ? 'font-semibold text-orange-900' : 'text-gray-700'}`} data-testid={`resync-total-${n}`}>
                            <span>{n}</span>
                            <span>{b} → {a}{changed ? ` (${a - b > 0 ? '+' : ''}${a - b})` : ''}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
                <p className="text-xs text-orange-700 mt-2">Reflects every suggested addition below. Unchecked orders are excluded when you apply.</p>
              </div>

              {/* Orders to add (selectable) */}
              <div data-testid="resync-additions">
                <p className="text-sm font-semibold text-gray-900 mb-2">
                  Orders to add ({resyncData.additions?.length || 0})
                </p>
                {resyncData.additions?.length ? (
                  <div className="space-y-2">
                    {resyncData.additions.map((a) => (
                      <label
                        key={a.order_id}
                        className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                        data-testid={`resync-add-row-${a.order_id}`}
                      >
                        <Checkbox
                          checked={resyncSelectedAdds.has(a.order_id)}
                          onCheckedChange={() => toggleResyncAddition(a.order_id)}
                          className="mt-1"
                          data-testid={`resync-add-check-${a.order_id}`}
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{a.order_number}</span>
                            <span className="text-xs text-gray-500">{a.company_name}</span>
                            {a.is_custom && <Badge variant="outline" className="text-xs">Custom</Badge>}
                          </div>
                          <p className="text-xs text-gray-500 truncate">{a.items_summary}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500" data-testid="resync-no-additions">No new orders to add.</p>
                )}
              </div>

              {/* Orders whose lines changed after the batch was planned */}
              <div data-testid="resync-modifications">
                <p className="text-sm font-semibold text-gray-900 mb-2">
                  Orders edited since this batch was planned ({resyncData.modifications?.length || 0})
                </p>
                {resyncData.modifications?.length ? (
                  <div className="space-y-2">
                    {resyncData.modifications.map((m) => (
                      <div key={m.order_id} className="p-3 border border-amber-200 bg-amber-50 rounded-lg" data-testid={`resync-modified-row-${m.order_id}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{m.order_number || m.order_id}</span>
                          <span className="text-xs text-gray-500">{m.company_name}</span>
                          {m.is_custom && <Badge variant="outline" className="text-xs">Custom</Badge>}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          <span className="line-through">{m.items_summary_before || '—'}</span>
                          {' → '}
                          <span className="font-medium text-amber-900">{m.items_summary_after || '—'}</span>
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500" data-testid="resync-no-modifications">No order has changed.</p>
                )}
              </div>

              {/* Orders to remove (automatic) */}
              <div data-testid="resync-removals">
                <p className="text-sm font-semibold text-gray-900 mb-2">
                  Orders to remove ({resyncData.removals?.length || 0})
                </p>
                {resyncData.removals?.length ? (
                  <div className="space-y-2">
                    {resyncData.removals.map((r) => (
                      <div key={r.order_id} className="flex items-center justify-between p-3 border border-red-100 bg-red-50 rounded-lg" data-testid={`resync-remove-row-${r.order_id}`}>
                        <div>
                          <span className="font-medium text-sm">{r.order_number || r.order_id}</span>
                          <span className="text-xs text-gray-500 ml-2">{r.company_name}</span>
                        </div>
                        <Badge variant="outline" className="text-xs capitalize">{(r.why || '').replace(/_/g, ' ')}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500" data-testid="resync-no-removals">No orders need removing.</p>
                )}
              </div>

              <p className="text-xs text-gray-400">{resyncData.unchanged_count || 0} order(s) unchanged.</p>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => { if (!resyncApplying) { setResyncOpen(false); setResyncData(null); setResyncBatchId(null); } }} data-testid="resync-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleApplyResync}
              disabled={resyncLoading || resyncApplying || !resyncData || (
                (resyncData.additions?.length || 0) === 0
                && (resyncData.removals?.length || 0) === 0
                && (resyncData.modifications?.length || 0) === 0
              )}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="resync-apply-btn"
            >
              {resyncApplying ? (<><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Applying…</>) : 'Apply changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5" />
              Production Checklist Preview
            </DialogTitle>
            <DialogDescription>
              Review the checklist before printing. Includes summary by donut type and per-order checklists.
            </DialogDescription>
          </DialogHeader>
          
          {printData && (
            <>
              {/* Printable Content - Preview version with Tailwind */}
              <div id="print-checklist-content" className="bg-white p-4 border rounded-lg text-sm">
                {/* Header */}
                <div className="text-center mb-6 border-b-2 border-black pb-4">
                  <h1 className="text-xl font-bold">MAD Donuts - Production Checklist</h1>
                  <div className="text-gray-600">
                    {new Date(printData.date + 'T12:00:00').toLocaleDateString('en-US', {
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-4 gap-3 mb-6">
                  <div className="border p-3 text-center rounded">
                    <div className="text-2xl font-bold">{printData.summary?.total_donuts || 0}</div>
                    <div className="text-xs text-gray-500">Total Donuts</div>
                  </div>
                  <div className="border p-3 text-center rounded">
                    <div className="text-2xl font-bold">{printData.summary?.total_orders || 0}</div>
                    <div className="text-xs text-gray-500">Total Orders</div>
                  </div>
                  <div className="border p-3 text-center rounded">
                    <div className="text-2xl font-bold">{printData.summary?.products?.length || 0}</div>
                    <div className="text-xs text-gray-500">Products</div>
                  </div>
                  <div className="border p-3 text-center rounded">
                    <div className="text-2xl font-bold">{printData.summary?.by_category?.length || 0}</div>
                    <div className="text-xs text-gray-500">Categories</div>
                  </div>
                </div>

                {/* Summary by Donut Type */}
                <h2 className="font-bold border-b-2 border-gray-800 pb-1 mb-3">Summary by Donut Type</h2>
                <div className="grid grid-cols-4 gap-2 mb-6">
                  {printData.summary?.by_category?.map((cat) => (
                    <div key={cat.category} className="border p-2 flex justify-between items-center rounded">
                      <span className="capitalize font-medium">{cat.category}</span>
                      <span className="text-lg font-bold">{cat.quantity}</span>
                    </div>
                  ))}
                </div>

                {/* Product Breakdown */}
                {printData.summary?.products?.length > 0 && (
                  <>
                    <h2 className="font-bold border-b-2 border-gray-800 pb-1 mb-3">Product Breakdown</h2>
                    <div className="grid grid-cols-4 gap-2 mb-6">
                      {printData.summary.products.map((product) => (
                        <div key={product.product_id || product.product_name} className="border p-2 flex justify-between items-center rounded">
                          <span className="font-medium text-xs">
                            {product.product_name}
                            {product.is_one_off && (
                              <span className="ml-1 text-[10px] uppercase tracking-wide text-gray-400">one-off</span>
                            )}
                          </span>
                          <span className="text-lg font-bold">{product.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Orders Checklist */}
                <h2 className="font-bold border-b-2 border-gray-800 pb-1 mb-3">
                  Order Checklists ({printData.orders_by_client?.length || 0})
                </h2>
                <div className="space-y-4">
                  {printData.orders_by_client?.map((order, orderIndex) => (
                    <div key={order.order_number || `order-${orderIndex}`} className="border rounded overflow-hidden">
                      <div className="bg-gray-100 p-3 border-b flex justify-between items-start">
                        <div>
                          <span className="font-bold text-base">
                            {locationHeading(order)}
                            {order.is_custom && (
                              <span className="ml-2 bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded">
                                CUSTOM
                              </span>
                            )}
                          </span>
                          {order.delivery_address && (
                            <div className="text-xs text-gray-700 mt-0.5">{order.delivery_address}</div>
                          )}
                          <div className="text-xs text-gray-600 mt-1">
                            {order.order_number && <span>Order: {order.order_number}</span>}
                            {order.delivery_time && <span> • Delivery: {order.delivery_time}</span>}
                            {!order.is_custom && <span> • Location: {locationLabel(order)}</span>}
                          </div>
                        </div>
                        <div className="font-bold text-lg">{order.total_donuts} donuts</div>
                      </div>
                      <div className="p-3">
                        {order.items.map((item, itemIndex) => (
                          <div key={`${order.order_id || order.order_number}-${item.product_id || item.product_name}-${itemIndex}`} className="flex items-center py-2 border-b border-dotted last:border-0">
                            <div className="w-5 h-5 border-2 border-gray-800 rounded mr-3 flex-shrink-0"></div>
                            <span className="flex-1">{item.product_name}</span>
                            <span className="font-bold w-12 text-right">{item.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="mt-8 pt-3 border-t text-center text-xs text-gray-500">
                  Generated on {new Date().toLocaleString()} • MAD Donuts Production System
                </div>
              </div>

              {/* Print Button */}
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setPrintDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={executePrint} className="bg-orange-500 hover:bg-orange-600">
                  <Printer className="h-4 w-4 mr-2" />
                  Print Checklist
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffProduction;
